// 🔧 vendors.js
// ✅ Purpose: Public-facing vendor directory for a specific event
// ✅ Features:
// - Uses useEventContext() to load the current event
// - Loads vendor categories from /events/{eventId}/settings/voting
// - Loads vendors from /events/{eventId}/vendors
// - Groups vendors by vendor category
// - Vendors without a valid category are grouped into "Other Vendors"
// - Shows each vendor in a clean white card
// - Shows vendor note / directory content when configured
// - Public-facing page available to event attendees
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import "react-quill/dist/quill.snow.css";
import { sanitizeHtml } from "../../utils/sanitize_html";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const Vendors = () => {
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [vendors, setVendors] = useState([]);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [vendorsNote, setVendorsNote] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready && eventId) trackEvent({ action: GA_EVENTS.VENDOR_LIST_VIEWED, parameters: { event_slug: eventslug, event_id: eventId, source_page: window.location.pathname } });
  }, [ready, eventId, eventslug]);

  useEffect(() => {
    const loadVendorPageData = async () => {
      if (!eventId) return;

      setLoading(true);

      try {
        logger.log("🛍 Loading public vendors page", {
          eventId,
        });

        const settingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const settingsData = settingsSnap.exists()
          ? settingsSnap.data()
          : {};

        setVendorCategories(settingsData.vendorCategories || []);
        setVendorsNote(settingsData.vendorsNote || "");

        const vendorsSnap = await getDocs(
          collection(db, "events", eventId, "vendors")
        );

        const vendorList = vendorsSnap.docs
          .map((vendorDoc) => ({
            id: vendorDoc.id,
            ...vendorDoc.data(),
          }))
          .filter((vendor) => {
            const isLegacyVendor =
              vendor.paymentStatus === undefined &&
              vendor.approvalStatus === undefined &&
              vendor.isApproved === undefined;

            const isApprovedVendor =
              vendor.isApproved === true &&
              vendor.approvalStatus === "approved" &&
              ["paid", "waived", "not_required"].includes(
                vendor.paymentStatus
              );

            return isLegacyVendor || isApprovedVendor;
          })
          .sort((a, b) =>
            String(a.name || "").localeCompare(
              String(b.name || ""),
              undefined,
              {
                sensitivity: "base",
              }
            )
          );

        setVendors(vendorList);
        logger.log("✅ Public vendors loaded", {
          eventId,
          count: vendorList.length,
        });
      } catch (err) {
        logger.error("❌ Error loading public vendors page:", {
          value: err,
        });

        setVendors([]);
        setVendorCategories([]);
        setVendorsNote("");
      } finally {
        setLoading(false);
      }
    };

    loadVendorPageData();
  }, [eventId]);

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Vendors",
      path: `/${eventslug}/vendors`,
    },
  ];

  const approvedVendorsSeo = buildEventPageSeo({
    eventDoc,
    pageKey: "vendors",

    fallbackTitle: `${eventDoc?.name || "Event"} Vendors | Let's Go Vote`,

    fallbackDescription: `Meet the vendors at ${
      eventDoc?.name || "this event"
    }.`,
  });

  const vendorsSeo = {
    ...approvedVendorsSeo,
    path: `/${eventslug}/vendors`,
    breadcrumbs,
  };

  const vendorCategoryNames = vendorCategories.map((category) =>
    typeof category === "string" ? category : category.name
  );

  const vendorsByCategory = [
    ...vendorCategoryNames
      .map((categoryName) => ({
        categoryName,
        vendors: vendors.filter((vendor) => vendor.category === categoryName),
      }))
      .filter((group) => group.vendors.length > 0),

    {
      categoryName: "Other Vendors",
      vendors: vendors.filter(
        (vendor) =>
          !vendor.category ||
          !vendorCategoryNames.includes(vendor.category)
      ),
    },
  ].filter((group) => group.vendors.length > 0);

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Vendors..." />;
  }

  return (
    <>
      <SeoHead {...vendorsSeo} />

      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "1200px" }}>
          <div className="row mb-4">
            <div className="col text-start">
              <h1 className="display-5">🛍 Event Vendors</h1>

              <p className="lead text-muted mb-0">
                {eventDoc?.name
                  ? `Explore vendors participating in ${eventDoc.name}.`
                  : "Explore vendors participating in this event."}
              </p>
            </div>
          </div>

          {vendorsNote && (
            <div className="white-section bg-white p-4 rounded shadow-sm mb-4">
              <h4 className="fw-bold mb-3">🛍 Vendor Information</h4>

              <div
                className="ql-editor p-0"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(vendorsNote) }}
              />
            </div>
          )}

          {vendors.length === 0 ? (
            <div className="white-section bg-white p-4 rounded shadow-sm">
              <div className="alert alert-info mb-0">
                No vendors have been added for this event yet.
              </div>
            </div>
          ) : (
            <div className="row">
              {(vendorsByCategory.length > 0
                ? vendorsByCategory
                : [
                    {
                      categoryName: "Event Vendors",
                      vendors,
                    },
                  ]
              ).map((group) => (
                <div key={group.categoryName} className="col-12 mb-4">
                  <div className="card shadow-sm border-0 h-100 bg-white">
                    <div className="card-header bg-white border-0 pt-4 px-4">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                        <div>
                          <h4 className="fw-bold mb-1 text-dark">
                            🛍 {group.categoryName}
                          </h4>

                          <p className="text-muted mb-0">
                            Browse {group.categoryName.toLowerCase()} at this event.
                          </p>
                        </div>

                        <span className="badge bg-dark mt-2 mt-md-0">
                          {group.vendors.length} Vendor
                          {group.vendors.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>

                    <div className="card-body px-4 pb-4">
                      <div className="row g-4">
                        {group.vendors.map((vendor) => (
                          <div key={vendor.id} className="col-12 col-md-6">
                            <div className="bg-white border rounded shadow-sm p-3 h-100">
                              <Link
                                to={`/${eventslug}/vendors/${vendor.vendorSlug || vendor.id}`}
                                className="text-decoration-none"
                              >
                                <div
                                  className="d-flex align-items-center justify-content-center bg-white border rounded mb-3"
                                  style={{ minHeight: "130px" }}
                                >
                                  {vendor.logoUrl ? (
                                    <img
                                      src={vendor.logoUrl}
                                      alt={vendor.name || "Vendor logo"}
                                      className="img-fluid"
                                      style={{
                                        maxHeight: "110px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  ) : (
                                    <div className="text-muted small">
                                      No Logo
                                    </div>
                                  )}
                                </div>

                                <h5 className="fw-bold text-dark text-center mb-2">
                                  {vendor.name || "Unnamed Vendor"}
                                </h5>
                              </Link>

                              <div className="text-center mb-3">
                                {vendor.boothNumber && (
                                  <span className="badge bg-primary me-2">
                                    📍 {vendor.boothNumber}
                                  </span>
                                )}

                                {vendor.featuredVendor && (
                                  <span className="badge bg-success">
                                    Featured
                                  </span>
                                )}
                              </div>

                              {vendor.description && (
                                <div
                                  className="ql-editor small p-0 text-muted"
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(vendor.description),
                                  }}
                                />
                              )}

                              <div className="d-flex flex-wrap gap-2 mt-3">
                                <Link
                                  to={`/${eventslug}/vendors/${vendor.vendorSlug || vendor.id}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  View Vendor
                                </Link>

                                {vendor.url && (
                                  <a
                                    href={vendor.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-secondary btn-sm"
                                  >
                                    Website
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-4">
            <Link
              to={`/${eventslug}/event`}
              className="btn btn-outline-secondary"
            >
              ← Back to Event Home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Vendors;
